import { useEffect, useState } from "react";
import { Dices, Plus, X } from "lucide-react";
import { useStateMachineValue } from "@/core/arch/state_machine";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/base/dropdown-menu";
import type { MerchantSpec } from "@cpos/shared/src/dev-merchant/spec";
import {
  rollFirstName,
  rollLastName,
  rollPhone,
  rollAddress,
  emailFor,
} from "@cpos/shared/src/dev-merchant/generators";
import type { CreateMerchantBloc } from "@/application/tools/create_merchant_bloc";
import { SectionCard, FormField } from "./shared";
import { inputClass } from "./styles";

interface Props {
  draft: MerchantSpec;
  bloc: CreateMerchantBloc;
  disabled?: boolean;
}

export function IdentitySection({ draft, bloc, disabled }: Props) {
  const [{ count }] = useStateMachineValue(bloc);
  const bulk = count > 1;
  const locked = Boolean(disabled) || bulk;
  const { identity } = draft;

  function patch(next: Partial<typeof identity>) {
    bloc.setDraft({ identity: { ...identity, ...next } });
  }

  const ttp = draft.userSettings?.tapToPay ?? {
    previouslyEnabled: true,
    educationShown: 1,
  };

  function patchTtp(next: Partial<typeof ttp>) {
    bloc.setDraft({ userSettings: { tapToPay: { ...ttp, ...next } } });
  }

  return (
    <SectionCard
      title="Identity"
      onRoll={() => bloc.rollIdentitySection()}
      rollDisabled={locked}
    >
      {bulk && (
        <p className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-800">
          Count is greater than 1 — email local-part gets +1, +2, … and business
          name gets a numeric suffix per merchant. Identity fields are fixed for
          the batch.
        </p>
      )}

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <FormField label="Email">
          <div className="flex gap-1">
            <input
              className={inputClass}
              value={identity.email}
              onChange={(e) => patch({ email: e.target.value })}
              disabled={locked}
            />
            <DiceButton
              disabled={locked}
              onClick={() =>
                patch({
                  email: emailFor(identity.givenName, identity.familyName),
                })
              }
            />
          </div>
        </FormField>

        <FormField label="Phone">
          <div className="flex gap-1">
            <input
              className={inputClass}
              value={identity.phone}
              onChange={(e) => patch({ phone: e.target.value })}
              disabled={locked}
            />
            <DiceButton
              disabled={locked}
              onClick={() =>
                patch({ phone: rollPhone(draft.business.country ?? "CA") })
              }
            />
          </div>
        </FormField>

        <FormField label="Given name">
          <div className="flex gap-1">
            <input
              className={inputClass}
              value={identity.givenName}
              onChange={(e) => patch({ givenName: e.target.value })}
              disabled={locked}
            />
            <DiceButton
              disabled={locked}
              onClick={() => patch({ givenName: rollFirstName() })}
            />
          </div>
        </FormField>

        <FormField label="Family name">
          <div className="flex gap-1">
            <input
              className={inputClass}
              value={identity.familyName}
              onChange={(e) => patch({ familyName: e.target.value })}
              disabled={locked}
            />
            <DiceButton
              disabled={locked}
              onClick={() => patch({ familyName: rollLastName() })}
            />
          </div>
        </FormField>

        <FormField label="Password">
          <input
            className={`${inputClass} font-mono`}
            value={identity.password}
            onChange={(e) => patch({ password: e.target.value })}
            disabled={locked}
          />
        </FormField>

        <FormField label="Cognito groups">
          <CognitoGroupsField
            value={identity.cognitoGroups}
            onChange={(cognitoGroups) => patch({ cognitoGroups })}
            disabled={disabled}
          />
        </FormField>

        <FormField label="Address line 1" className="sm:col-span-2">
          <div className="flex gap-1">
            <input
              className={inputClass}
              value={identity.address.line1}
              onChange={(e) =>
                patch({
                  address: { ...identity.address, line1: e.target.value },
                })
              }
              disabled={locked}
            />
            <DiceButton
              disabled={locked}
              onClick={() =>
                patch({ address: rollAddress(draft.business.country ?? "CA") })
              }
            />
          </div>
        </FormField>

        <FormField label="City">
          <input
            className={inputClass}
            value={identity.address.city}
            onChange={(e) =>
              patch({ address: { ...identity.address, city: e.target.value } })
            }
            disabled={locked}
          />
        </FormField>

        <FormField label="State/Province">
          <input
            className={inputClass}
            value={identity.address.state}
            onChange={(e) =>
              patch({ address: { ...identity.address, state: e.target.value } })
            }
            disabled={locked}
          />
        </FormField>

        <FormField label="Postal code">
          <input
            className={inputClass}
            value={identity.address.postalCode}
            onChange={(e) =>
              patch({
                address: { ...identity.address, postalCode: e.target.value },
              })
            }
            disabled={locked}
          />
        </FormField>

        <FormField label="Country">
          <select
            className={inputClass}
            value={identity.address.country}
            onChange={(e) =>
              patch({
                address: {
                  ...identity.address,
                  country: e.target.value as "CA" | "US",
                },
              })
            }
            disabled={locked}
          >
            <option value="CA">CA</option>
            <option value="US">US</option>
          </select>
        </FormField>

        <FormField label="Date of birth (Y / M / D)">
          <div className="flex gap-1">
            <input
              type="number"
              className={inputClass}
              value={identity.dob.year}
              onChange={(e) =>
                patch({
                  dob: { ...identity.dob, year: Number(e.target.value) },
                })
              }
              disabled={locked}
            />
            <input
              type="number"
              className={inputClass}
              value={identity.dob.month}
              onChange={(e) =>
                patch({
                  dob: { ...identity.dob, month: Number(e.target.value) },
                })
              }
              disabled={locked}
            />
            <input
              type="number"
              className={inputClass}
              value={identity.dob.day}
              onChange={(e) =>
                patch({ dob: { ...identity.dob, day: Number(e.target.value) } })
              }
              disabled={locked}
            />
          </div>
        </FormField>

        <FormField label="Timezone">
          <input
            className={inputClass}
            value={identity.timezone}
            onChange={(e) => patch({ timezone: e.target.value })}
            disabled={locked}
          />
        </FormField>

        <FormField
          label="Tap to Pay"
          hint="Both on by default so merchants skip the v1 education gate"
        >
          <div className="flex flex-col gap-1.5 pt-1">
            <label className="text-foreground flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={ttp.educationShown > 0}
                onChange={(e) =>
                  patchTtp({ educationShown: e.target.checked ? 1 : 0 })
                }
                disabled={disabled}
              />
              Education shown
            </label>
            <label className="text-foreground flex items-center gap-2 text-xs">
              <input
                type="checkbox"
                checked={ttp.previouslyEnabled}
                onChange={(e) =>
                  patchTtp({ previouslyEnabled: e.target.checked })
                }
                disabled={disabled}
              />
              Previously enabled
            </label>
          </div>
        </FormField>
      </div>
    </SectionCard>
  );
}

const KNOWN_COGNITO_GROUPS = [
  "Subscriber-TapToPay",
  "Subscriber-PoyntTerminal",
  "CPOS-Developers",
  "CPOS-Admin",
];

/**
 * Cognito group picker rendered as a pill list with a trailing "+" button.
 * The "+" opens a menu of not-yet-added groups and disables once every
 * option is added. Unknown groups carried in from a preset stay in the menu
 * after removal so unchecking one is reversible without retyping it.
 */
function CognitoGroupsField({
  value,
  onChange,
  disabled,
}: {
  value: string[];
  onChange: (groups: string[]) => void;
  disabled?: boolean;
}) {
  const [extras, setExtras] = useState<string[]>(() =>
    value.filter((g) => !KNOWN_COGNITO_GROUPS.includes(g)),
  );

  useEffect(() => {
    const unknown = value.filter((g) => !KNOWN_COGNITO_GROUPS.includes(g));
    setExtras((prev) => {
      const fresh = unknown.filter((g) => !prev.includes(g));
      return fresh.length ? [...prev, ...fresh] : prev;
    });
  }, [value]);

  const available = [...KNOWN_COGNITO_GROUPS, ...extras].filter(
    (g) => !value.includes(g),
  );

  return (
    <ul className="flex min-h-8 flex-wrap items-center gap-1">
      {value.map((group) => (
        <li
          key={group}
          className="bg-muted text-foreground flex items-center gap-1 rounded-full py-0.5 pr-1 pl-2 text-xs"
        >
          {group}
          <button
            type="button"
            onClick={() => onChange(value.filter((g) => g !== group))}
            disabled={disabled}
            title={`Remove ${group}`}
            className="text-subtle-foreground hover:bg-muted hover:text-foreground rounded-full p-0.5 transition disabled:cursor-not-allowed disabled:opacity-40"
          >
            <X className="h-3 w-3" />
          </button>
        </li>
      ))}
      <li>
        <DropdownMenu>
          <DropdownMenuTrigger asChild disabled={disabled || !available.length}>
            <button
              type="button"
              title={available.length ? "Add group" : "All groups added"}
              className="border-border text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground rounded-full border p-1 transition disabled:cursor-not-allowed disabled:opacity-40"
            >
              <Plus className="h-3.5 w-3.5" />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="w-56">
            {available.map((group) => (
              <DropdownMenuItem
                key={group}
                onSelect={() => onChange([...value, group])}
              >
                {group}
              </DropdownMenuItem>
            ))}
          </DropdownMenuContent>
        </DropdownMenu>
      </li>
    </ul>
  );
}

function DiceButton({
  onClick,
  disabled,
}: {
  onClick: () => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title="Randomize"
      className="border-border text-muted-foreground hover:border-muted-foreground/40 hover:text-foreground shrink-0 rounded-md border px-2 transition disabled:cursor-not-allowed disabled:opacity-40"
    >
      <Dices className="h-3.5 w-3.5" />
    </button>
  );
}
